// ============================================================================
// PresenterManageModal — add / edit / archive presenters in the library.
// ============================================================================
// Opened from PresenterLibrary's "Manage library" button. Shows the current
// presenter list with inline Edit and Archive controls, plus an "Add new"
// button that reveals a blank form. After any successful mutation we call
// onRefresh so the parent picker reloads its list.
//
// Photo handling: photo_url is a plain text input for now (paste a path like
// /presenters/kayla.jpg or a full https URL). Phase 3d-1f will replace this
// with an S3 upload widget.
// ============================================================================

import { useState } from 'react';
import { Pencil, Trash2, Plus, Loader2, Check, X } from 'lucide-react';
import { Modal } from './Modal';
import { api, type Presenter, type PresenterInput } from '../api';
import { UploadPhoto } from './UploadPhoto';

interface Props {
  open: boolean;
  onClose: () => void;
  token: string;
  presenters: Presenter[];
  onRefresh: () => void;
  /** Render as a flat in-flow panel (no overlay). Used by the admin sidebar. */
  inline?: boolean;
}

interface FormState {
  presenterId: number | null;  // null = new
  name: string;
  role: string;
  photoUrl: string;
  bio: string;
  displayOrder: number;
}

const EMPTY_FORM: FormState = {
  presenterId: null,
  name: '',
  role: '',
  photoUrl: '',
  bio: '',
  displayOrder: 0,
};

export function PresenterManageModal({ open, onClose, token, presenters, onRefresh, inline = false }: Props) {
  const [form, setForm] = useState<FormState | null>(null);
  const [busyId, setBusyId] = useState<number | 'new' | null>(null);
  const [error, setError] = useState<string>('');

  const startNew = () => {
    setError('');
    setForm({ ...EMPTY_FORM, displayOrder: (presenters.at(-1)?.display_order ?? 0) + 10 });
  };

  const startEdit = (p: Presenter) => {
    setError('');
    setForm({
      presenterId: p.presenter_id,
      name: p.name,
      role: p.role ?? '',
      photoUrl: p.photo_url ?? '',
      bio: p.bio ?? '',
      displayOrder: p.display_order,
    });
  };

  const cancelForm = () => {
    setForm(null);
    setError('');
  };

  const save = async () => {
    if (!form) return;
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    setError('');
    setBusyId(form.presenterId ?? 'new');
    const payload: PresenterInput = {
      name: form.name.trim(),
      role: form.role.trim() || null,
      photo_url: form.photoUrl.trim() || null,
      bio: form.bio.trim() || null,
      display_order: Number(form.displayOrder) || 0,
    };
    try {
      if (form.presenterId == null) {
        await api.createPresenter(token, payload);
      } else {
        await api.updatePresenter(token, form.presenterId, payload);
      }
      setForm(null);
      onRefresh();
    } catch (err: any) {
      setError(err?.message || 'Could not save presenter.');
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (p: Presenter) => {
    const yes = window.confirm(
      `Archive "${p.name}"? They'll disappear from the picker but past events that already imported their bio keep their existing markdown.`,
    );
    if (!yes) return;
    setBusyId(p.presenter_id);
    try {
      await api.archivePresenter(token, p.presenter_id);
      onRefresh();
    } catch (err: any) {
      alert(err?.message || 'Could not archive presenter.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Manage presenter library" maxWidth="max-w-3xl" inline={inline}>
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
            {error}
          </div>
        )}

        {form == null && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={startNew}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-uif-green-dark"
            >
              <Plus className="h-4 w-4" />
              Add new presenter
            </button>
          </div>
        )}

        {form != null && (
          <fieldset className="border border-uif-green/30 rounded-md p-4 space-y-3 bg-uif-green/5">
            <legend className="px-1 text-sm font-semibold text-uif-green-dark">
              {form.presenterId == null ? 'New presenter' : 'Edit presenter'}
            </legend>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Name *</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Kayla McMinn"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Role / title</span>
              <input
                type="text"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="e.g. Food Safety Specialist · Day 1 lead"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>

            <div>
              <span className="block text-xs font-medium text-gray-700 mb-1">Photo</span>
              <UploadPhoto
                token={token}
                value={form.photoUrl}
                onChange={(url) => setForm({ ...form, photoUrl: url })}
                destination="presenters"
                shape="circle"
              />
              <label className="block mt-2">
                <span className="block text-[11px] font-medium text-gray-500 mb-1">…or paste a URL manually</span>
                <input
                  type="text"
                  value={form.photoUrl}
                  onChange={(e) => setForm({ ...form, photoUrl: e.target.value })}
                  placeholder="/presenters/kayla.jpg or https://..."
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
                />
              </label>
            </div>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Bio</span>
              <textarea
                rows={6}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Markdown allowed. Single-paragraph bio renders best."
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none font-mono"
              />
            </label>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Display order</span>
              <input
                type="number"
                value={form.displayOrder}
                onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) || 0 })}
                className="w-32 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
              <p className="text-[11px] text-gray-500 mt-0.5">Lower numbers appear first.</p>
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={cancelForm}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busyId !== null}
                className="inline-flex items-center gap-1 rounded-md bg-uif-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-uif-green-dark disabled:bg-gray-300"
              >
                {busyId !== null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                {form.presenterId == null ? 'Add presenter' : 'Save changes'}
              </button>
            </div>
          </fieldset>
        )}

        {form == null && (
          <div className="space-y-2">
            {presenters.length === 0 && (
              <p className="text-sm text-gray-500 italic text-center py-4">
                No presenters yet. Click "Add new presenter" above to start the library.
              </p>
            )}
            {presenters.map((p) => (
              <div
                key={p.presenter_id}
                className="flex items-center gap-3 border border-gray-200 rounded-md p-3 hover:border-uif-green/40"
              >
                {p.photo_url ? (
                  <img
                    src={p.photo_url}
                    alt={p.name}
                    className="h-12 w-12 rounded-full object-cover ring-1 ring-uif-green/20 flex-shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-uif-green/10 text-uif-green-dark flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {p.name.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900 truncate">{p.name}</div>
                  {p.role && <div className="text-xs text-gray-600 truncate">{p.role}</div>}
                </div>
                <button
                  type="button"
                  onClick={() => startEdit(p)}
                  disabled={busyId !== null}
                  className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => archive(p)}
                  disabled={busyId !== null}
                  className="inline-flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-3 w-3" />
                  Archive
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
